import { METRES_PER_DEGREE_LAT, METRES_PER_DEGREE_LNG } from "./metres";
// Why this exists: on a pure-categorical selection (Pure Soil Type) every cell of a class has an
// IDENTICAL one-hot vector, so k-means past the class count can only split classes as seed-noise
// confetti. Stepping the count must instead do something a farmer can read.

export interface CellPt {
  x: number;
  y: number;
}

/* * cell lng/lat centroids → local meters (equirectangular; only relative distances matter) */
export function toMeters(centroids: { lng: number; lat: number }[]): CellPt[] {
  const lat0 = centroids[0]?.lat ?? 0;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat0 * Math.PI) / 180);
  return centroids.map((c) => ({
    x: c.lng * kx,
    y: c.lat * METRES_PER_DEGREE_LAT,
  }));
}

/* * NW→SE diagonal sweep key: west (small x) and north (large y) sort FIRST */
const sweep = (p: CellPt) => p.x - p.y;
const halfUp = (v: number) => Math.floor(v + 0.5);

/* * connected components of `cells` under `adj` (rook-4), BFS in ascending index order — deterministic */
export function sectionsOf(cells: number[], adj: number[][]): number[][] {
  const inZone = new Set(cells);
  const seen = new Set<number>();
  const out: number[][] = [];
  for (const c of [...cells].sort((a, b) => a - b)) {
    if (seen.has(c)) continue;
    const comp: number[] = [];
    const queue = [c];
    seen.add(c);
    while (queue.length) {
      const u = queue.shift()!;
      comp.push(u);
      for (const v of adj[u] ?? [])
        if (inZone.has(v) && !seen.has(v)) {
          seen.add(v);
          queue.push(v);
        }
    }
    out.push(comp);
  }
  return out;
}

/* * the section's NW-most cell under the diagonal sweep (ties: x, then index) — its ordering key */
function nwCellOf(section: number[], pts: CellPt[]): number {
  let best = section[0]!;
  for (const i of section) {
    const a = sweep(pts[i]!),
      b = sweep(pts[best]!);
    if (
      a < b ||
      (a === b &&
        (pts[i]!.x < pts[best]!.x || (pts[i]!.x === pts[best]!.x && i < best)))
    )
      best = i;
  }
  return best;
}

function centroidOf(cells: number[], pts: CellPt[]): CellPt {
  let x = 0,
    y = 0;
  for (const i of cells) {
    x += pts[i]!.x;
    y += pts[i]!.y;
  }
  return { x: x / cells.length, y: y / cells.length };
}

/*
 *
 * Split C between anchors A and B: A takes `take` cells, the ones CLOSEST to A relative to B
 * (lowest dA/dB). Compared by cross-multiplied SQUARED distances — no division, no dB=0 singularity
 * (a cell AT anchor B sorts last for A, which is exactly right); ties: sweep, then index.
 */
function takeNearestByRatio(
  C: number[],
  take: number,
  a: CellPt,
  b: CellPt,
  pts: CellPt[],
): number[] {
  const d2 = (p: CellPt, q: CellPt) => (p.x - q.x) ** 2 + (p.y - q.y) ** 2;
  const dA = new Map(C.map((i) => [i, d2(pts[i]!, a)]));
  const dB = new Map(C.map((i) => [i, d2(pts[i]!, b)]));
  const sorted = [...C].sort((i, j) => {
    const diff = dA.get(i)! * dB.get(j)! - dA.get(j)! * dB.get(i)!;
    if (diff !== 0) return diff < 0 ? -1 : 1;
    const s = sweep(pts[i]!) - sweep(pts[j]!);
    if (s !== 0) return s;
    return i - j;
  });
  return sorted.slice(0, take);
}

/*
 *
 * Equal-area LONG-AXIS halves of a connected (or fallback) cell set: project on the principal axis
 * take the NW-ish half.
 */
export function longAxisHalf(cells: number[], pts: CellPt[]): number[] {
  const n = cells.length;
  const m = centroidOf(cells, pts);
  let sxx = 0,
    sxy = 0,
    syy = 0;
  for (const i of cells) {
    const dx = pts[i]!.x - m.x,
      dy = pts[i]!.y - m.y;
    sxx += dx * dx;
    sxy += dx * dy;
    syy += dy * dy;
  }
  let ax: number, ay: number;
  if (Math.abs(sxy) < 1e-9) {
    [ax, ay] = sxx >= syy ? [1, 0] : [0, 1];
  } else {
    const lam = (sxx + syy) / 2 + Math.sqrt(((sxx - syy) / 2) ** 2 + sxy * sxy);
    const len = Math.hypot(lam - syy, sxy);
    ax = (lam - syy) / len;
    ay = sxy / len;
  }
  // orient so ascending projection starts at the NW-ish end (axis · (1,−1) ≥ 0), sign fully pinned
  const s = ax - ay;
  if (s < 0 || (s === 0 && (ax < 0 || (ax === 0 && ay < 0)))) {
    ax = -ax;
    ay = -ay;
  }
  const sorted = [...cells].sort((i, j) => {
    const pi = pts[i]!.x * ax + pts[i]!.y * ay,
      pj = pts[j]!.x * ax + pts[j]!.y * ay;
    if (pi !== pj) return pi - pj;
    const sw = sweep(pts[i]!) - sweep(pts[j]!);
    if (sw !== 0) return sw;
    return i - j;
  });
  return sorted.slice(0, halfUp(n / 2));
}

/* * ONE split of one zone's cells → the cell set of the NEW zone (null = unsplittable) */
export function splitZoneOnce(
  cells: number[],
  pts: CellPt[],
  adj: number[][],
): number[] | null {
  const n = cells.length;
  if (n < 2) return null;
  const sections = sectionsOf(cells, adj);
  if (sections.length === 1) return longAxisHalf(cells, pts);

  // NW→SE section order: by each section's NW-most cell under the sweep (ties: x, then index)
  const ordered = [...sections].sort((sa, sb) => {
    const ia = nwCellOf(sa, pts),
      ib = nwCellOf(sb, pts);
    const d = sweep(pts[ia]!) - sweep(pts[ib]!);
    if (d !== 0) return d;
    if (pts[ia]!.x !== pts[ib]!.x) return pts[ia]!.x - pts[ib]!.x;
    return ia - ib;
  });

  const acc: number[] = [];
  for (let si = 0; si < ordered.length; si++) {
    const sec = ordered[si]!;
    const T = acc.length;
    const Tnew = T + sec.length;
    // CLOSED band in integer cell counts: 30% ≤ Tnew ≤ 70% → the accumulated sections ARE the new zone
    if (10 * Tnew >= 3 * n && 10 * Tnew <= 7 * n) return [...acc, ...sec];
    // the JUMP: strictly under 30 → strictly over 70. A = NW accumulation, C = this section, B = the rest
    if (10 * T < 3 * n && 10 * Tnew > 7 * n) {
      const B = ordered.slice(si + 1).flat();
      const target = halfUp(n / 2);
      const take = Math.max(1, Math.min(target - acc.length, sec.length - 1));
      // empty A/B borrow C's own NW-most / SE-most cell as the missing anchor
      const anchorA = acc.length
        ? centroidOf(acc, pts)
        : pts[nwCellOf(sec, pts)]!;
      const anchorB = B.length ? centroidOf(B, pts) : pts[seCellOf(sec, pts)]!;
      return [...acc, ...takeNearestByRatio(sec, take, anchorA, anchorB, pts)];
    }
    acc.push(...sec);
  }
  // unreachable zone-relative (prefix sums reach 100%, which lands or jumps) — defensive fallback
  return longAxisHalf(cells, pts);
}

/* * the section's SE-most cell (mirror of nwCellOf) */
function seCellOf(section: number[], pts: CellPt[]): number {
  let best = section[0]!;
  for (const i of section) {
    const a = sweep(pts[i]!),
      b = sweep(pts[best]!);
    if (
      a > b ||
      (a === b &&
        (pts[i]!.x > pts[best]!.x || (pts[i]!.x === pts[best]!.x && i > best)))
    )
      best = i;
  }
  return best;
}

const distinctZones = (labels: number[]) => {
  const s = new Set<number>();
  for (const l of labels) if (l >= 0) s.add(l);
  return s;
};

/* * cells per zone label, ascending label order */
function zonesByCells(labels: number[]): Map<number, number[]> {
  const m = new Map<number, number[]>();
  for (let i = 0; i < labels.length; i++) {
    if (labels[i]! < 0) continue;
    const arr = m.get(labels[i]!);
    if (arr) arr.push(i);
    else m.set(labels[i]!, [i]);
  }
  return m;
}

/*
 *
 * Split zones (biggest first — integer cell count, ties → lowest label) until `targetK` zones exist.
 * New zones get label max+1, existing labels never move — the map stays visually stable while stepping.
 */
export function splitToCount(
  labels: number[],
  centroids: { lng: number; lat: number }[],
  adj: number[][],
  targetK: number,
): number[] {
  const out = labels.slice();
  const pts = toMeters(centroids);
  let distinct = distinctZones(out).size;
  while (distinct < targetK) {
    const zones = zonesByCells(out);
    let pick = -1,
      pickCells: number[] = [];
    for (const [lab, cells] of [...zones.entries()].sort(
      (a, b) => a[0] - b[0],
    )) {
      if (cells.length >= 2 && cells.length > pickCells.length) {
        pick = lab;
        pickCells = cells;
      }
    }
    if (pick < 0) break; // nothing splittable left (all zones single-cell)
    const newCells = splitZoneOnce(pickCells, pts, adj);
    if (!newCells || !newCells.length || newCells.length >= pickCells.length)
      break;
    // loop, not Math.max(...spread) — the spread throws RangeError past the engine arg limit (~65k
    // cells ≈ a 1,000+ ac field), the same trap nextZoneId already avoids
    let newLabel = 1;
    for (const l of out) if (l >= newLabel) newLabel = l + 1;
    for (const i of newCells) out[i] = newLabel;
    distinct++;
  }
  return out;
}

export function mergeToCount(
  labels: number[],
  adj: number[][],
  targetK: number,
): number[] {
  const out = labels.slice();
  let distinct = distinctZones(out).size;
  while (distinct > Math.max(1, targetK)) {
    const zones = zonesByCells(out);
    const bySize = [...zones.entries()].sort(
      (a, b) => a[1].length - b[1].length || a[0] - b[0],
    );
    let merged = false;
    for (const [lab, cells] of bySize) {
      const border = new Map<number, number>();
      for (const i of cells)
        for (const j of adj[i] ?? []) {
          const l = out[j]!;
          if (l >= 0 && l !== lab) border.set(l, (border.get(l) ?? 0) + 1);
        }
      let best = -1,
        bestCount = 0;
      for (const [l, c] of [...border.entries()].sort((a, b) => a[0] - b[0])) {
        if (c > bestCount) {
          best = l;
          bestCount = c;
        }
      }
      if (best < 0) continue; // isolated zone — try the next smallest
      for (const i of cells) out[i] = best;
      distinct--;
      merged = true;
      break;
    }
    if (!merged) break;
  }
  return out;
}
